/**
 * @fileoverview ビューポート管理システム - モバイル端末でのアドレスバー伸縮に対応した動的ビューポート管理
 * PWA/TWA対応のための基盤機能
 */

import { MinimalErrorTracker } from '../shared/log.js';

const ATTR_DATA_SCROLL_Y = 'data-scroll-y';

/**
 * ビューポート高さを動的に更新
 * アドレスバーの表示/非表示による高さ変動に対応
 */
function updateViewportHeight() {
  try {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  } catch (error) {
    MinimalErrorTracker.warn('SYSTEM', 'Failed to update viewport height', error);
  }
}

/**
 * PWA表示モードを検出
 * @returns {string} 'standalone' | 'minimal-ui' | 'fullscreen' | 'browser'
 */
function detectDisplayMode() {
  try {
    // PWAスタンドアロンモード検出
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return 'standalone';
    }

    // TWA検出
    if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      return 'minimal-ui';
    }

    // フルスクリーンモード検出
    if (window.matchMedia('(display-mode: fullscreen)').matches) {
      return 'fullscreen';
    }

    // 通常のブラウザモード
    return 'browser';
  } catch (error) {
    MinimalErrorTracker.warn('SYSTEM', 'Failed to detect display mode', error);
    return 'browser';
  }
}

/**
 * スクロールロック機能
 * モーダル・ドロワー表示時の背景スクロール防止
 */
const scrollLock = {
  /**
   * スクロールをロック
   */
  lock() {
    try {
      // 現在のスクロール位置を保存
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('body--locked');

      // iOS Safari対応: position: fixed時のスクロール位置保持
      document.body.setAttribute(ATTR_DATA_SCROLL_Y, scrollY.toString());
    } catch (error) {
      MinimalErrorTracker.warn('SYSTEM', 'Failed to lock scroll', error);
    }
  },

  /**
   * スクロールロックを解除
   */
  unlock() {
    try {
      document.body.classList.remove('body--locked');

      // 保存されたスクロール位置を復元
      const scrollY = document.body.getAttribute(ATTR_DATA_SCROLL_Y);
      document.body.style.top = '';
      document.body.removeAttribute(ATTR_DATA_SCROLL_Y);

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10));
      }
    } catch (error) {
      MinimalErrorTracker.warn('SYSTEM', 'Failed to unlock scroll', error);
    }
  },
};

/**
 * 初期化処理
 */
function initializeViewport() {
  // 初回ビューポート高さ設定
  updateViewportHeight();

  // 表示モード検出とbodyクラス設定
  const displayMode = detectDisplayMode();
  document.body.classList.add(`display-mode--${displayMode}`);

  // イベントリスナー登録
  window.addEventListener('resize', updateViewportHeight);
  window.addEventListener('orientationchange', () => {
    // orientationchange後の遅延実行
    setTimeout(updateViewportHeight, 100);
  });

  // ビューポート変更の監視
  if (window.matchMedia) {
    const displayModeQueries = [
      '(display-mode: standalone)',
      '(display-mode: minimal-ui)',
      '(display-mode: fullscreen)',
    ];

    displayModeQueries.forEach((query) => {
      const mq = window.matchMedia(query);
      mq.addEventListener('change', () => {
        // 表示モード変更時の再初期化
        const newMode = detectDisplayMode();
        document.body.className = document.body.className.replace(/display-mode--\w+/g, '');
        document.body.classList.add(`display-mode--${newMode}`);
      });
    });
  }
}

// DOM読み込み完了後に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeViewport);
} else {
  initializeViewport();
}

// エクスポート
export { updateViewportHeight, detectDisplayMode, scrollLock };
